import { AGENTPLANE_ORIGIN } from './support-origin';

let publicKeyRequest;
let cleanup = Promise.resolve();
const OWNER_KEY = 'derasa-support-push-owner';
const CONSENT_KEY = 'derasa-support-push-consent-owner';

function decodeKey(host, key) {
    const value = host.atob(key.replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(value, c => c.charCodeAt(0));
}

export function pushSupported(host) {
    return !!(host.isSecureContext && host.navigator.serviceWorker && host.PushManager && host.Notification);
}

export function clearSupportPush(host) {
    cleanup = cleanup.then(async () => {
        try {
            // Never remove unrelated subscriptions or unregister the offline worker.
            if (!host.localStorage.getItem(OWNER_KEY) || !host.navigator.serviceWorker) return;
            const registration = await host.navigator.serviceWorker.getRegistration('/');
            const subscription = registration && await registration.pushManager.getSubscription();
            if (subscription && !(await subscription.unsubscribe())) return;
            host.localStorage.removeItem(OWNER_KEY);
        } catch (error) { /* Browser restrictions must not block logout. */ }
    });
    return cleanup;
}

function bounded(host, promise) {
    let timer;
    const timeout = new Promise((resolve, reject) => {
        timer = host.setTimeout(() => reject(new Error('Push service timed out')), 15000);
    });
    return Promise.race([promise, timeout]).finally(() => host.clearTimeout(timer));
}

async function publicKey(host, configured) {
    if (configured) return configured;
    // Agentplane currently publishes its VAPID public key in its CORS-enabled
    // embed loader, rather than a JSON endpoint. Read it without executing code.
    if (!publicKeyRequest) {
        let timer;
        const request = host.fetch(AGENTPLANE_ORIGIN + '/embed.js', { credentials: 'omit' });
        const timeout = new Promise((resolve, reject) => { timer = host.setTimeout(() => reject(new Error('Push configuration timed out')), 10000); });
        publicKeyRequest = Promise.race([request, timeout])
            .then(response => { if (!response.ok) throw new Error('Push configuration unavailable'); return response.text(); })
            .then(script => {
                const match = script.match(/var PUSH_KEY = "([A-Za-z0-9_-]+)";/);
                if (!match) throw new Error('Push public key unavailable');
                return match[1];
            }).catch(error => { publicKeyRequest = null; throw error; }).finally(() => host.clearTimeout(timer));
    }
    return publicKeyRequest;
}

export function createPushBridge({ host, config, post, owner, onStatus = () => {} }) {
    let disposed = false;
    let generation = 0;
    let pending;
    let currentOwner = owner;
    let failed = false;
    let subscribed = false;
    let requiresOptIn = false;
    try {
        const saved = host.localStorage.getItem(OWNER_KEY);
        const consent = host.localStorage.getItem(CONSENT_KEY);
        requiresOptIn = (!!saved && saved !== owner) || (!!consent && consent !== owner);
    } catch (error) { /* optional */ }
    if (requiresOptIn) clearSupportPush(host);
    const available = pushSupported(host) && config.pushEnabled !== false;
    function status() {
        const value = { available, subscribed, needsOptIn: requiresOptIn, permission: available ? host.Notification.permission : 'unsupported', error: failed };
        if (!disposed) { post({ type: 'agentplane:push-status', ...value }); onStatus(value); }
    }
    async function subscribe(prompt) {
        if (pending || !available || disposed) { status(); return pending; }
        if (!prompt && requiresOptIn) return;
        if (prompt) requiresOptIn = false;
        failed = false;
        const run = generation;
        const expectedOwner = currentOwner;
        // Ask synchronously from the explicit host button (before any await).
        const permission = prompt && host.Notification.permission === 'default'
            ? host.Notification.requestPermission() : Promise.resolve(host.Notification.permission);
        pending = (async () => {
            try {
                if (await permission !== 'granted' || disposed || run !== generation) return;
                await cleanup;
                const key = await publicKey(host, config.pushPublicKey);
                if (disposed || run !== generation) return;
                const registration = await bounded(host, host.navigator.serviceWorker.register('/agentplane-sw.js', { scope: '/' }));
                await bounded(host, host.navigator.serviceWorker.ready);
                const worker = registration.installing || registration.waiting;
                if (worker && worker.state !== 'activated') {
                    await new Promise((resolve, reject) => {
                        const timeout = host.setTimeout(() => { worker.removeEventListener('statechange', changed); reject(new Error('Push worker activation timed out')); }, 15000);
                        function changed() {
                            if (worker.state === 'activated' || worker.state === 'redundant') {
                                host.clearTimeout(timeout);
                                worker.removeEventListener('statechange', changed);
                                if (worker.state === 'activated') resolve(); else reject(new Error('Push worker could not activate'));
                            }
                        }
                        worker.addEventListener('statechange', changed);
                        changed();
                    });
                }
                if (disposed || run !== generation) return;
                let subscription = await registration.pushManager.getSubscription();
                const savedOwner = host.localStorage.getItem(OWNER_KEY);
                if (subscription && savedOwner !== expectedOwner) {
                    // A subscription to another VAPID application must remain untouched.
                    const existingKey = subscription.options && subscription.options.applicationServerKey;
                    const bytes = decodeKey(host, key);
                    const matches = existingKey && new Uint8Array(existingKey).every((v, i) => v === bytes[i]) && existingKey.byteLength === bytes.length;
                    if (!savedOwner && !matches) throw new Error('Another push service is already configured');
                    if (!(await subscription.unsubscribe())) throw new Error('Previous push subscription could not be cleared');
                    subscription = null;
                }
                if (disposed || run !== generation) return;
                if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decodeKey(host, key) });
                if (disposed || run !== generation) { await subscription.unsubscribe(); return; }
                host.localStorage.setItem(OWNER_KEY, expectedOwner);
                host.localStorage.setItem(CONSENT_KEY, expectedOwner);
                subscribed = true;
                post({ type: 'agentplane:push-subscription', subscription: subscription.toJSON(), hostUrl: host.location.origin + '/help?supportChat=1' });
            } catch (error) {
                if (!disposed && run === generation) failed = true;
            } finally {
                pending = null;
                status();
            }
        })();
        return pending;
    }
    return {
        ready() { status(); if (available && !requiresOptIn && host.Notification.permission === 'granted') return subscribe(false); },
        enable() { return subscribe(true); },
        async changeOwner(value) {
            if (value === currentOwner) return;
            currentOwner = value;
            subscribed = false;
            requiresOptIn = true;
            generation++;
            await clearSupportPush(host);
            // The new owner must explicitly opt in again; no inherited subscription.
            status();
        },
        dispose() { disposed = true; generation++; },
    };
}
